import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const FACTS = [
  { value: "2019", label: "gegründet in" },
  { value: "120+", label: "Projekte live" },
  { value: "6", label: "Köpfe im Team" },
];

/**
 * Helle Über-uns-Sektion zwischen den beiden dunklen Sektionen.
 * Asymmetrie entsteht durch schräge Sektionskanten, versetztes Raster
 * und ein aus dem Raster herausragendes Bildfeld (Platzhalter).
 */
export const AboutSection = () => (
  <section
    id="ueber-uns"
    className="relative isolate overflow-hidden bg-light text-light-foreground"
    style={{
      clipPath:
        "polygon(0 3.5vw, 100% 0, 100% 100%, 0 calc(100% - 2.2vw))",
    }}
  >
    {/* Asymmetrischer Hintergrund */}
    <div
      aria-hidden
      className="absolute inset-0 -z-10 bg-gradient-light"
    />
    <div
      aria-hidden
      className="absolute -left-32 top-[-12%] -z-10 h-[420px] w-[620px] -rotate-12 rounded-[40%] bg-[radial-gradient(closest-side,hsl(214_80%_72%/0.28),transparent)] blur-2xl"
    />
    <div
      aria-hidden
      className="absolute bottom-[-18%] right-[-8%] -z-10 h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,hsl(265_70%_74%/0.22),transparent)] blur-2xl"
    />
    <div
      aria-hidden
      className="absolute right-[14%] top-0 -z-10 hidden h-40 w-px bg-[linear-gradient(180deg,hsl(222_50%_40%/0.35),transparent)] lg:block"
    />

    <div className="mx-auto max-w-[1500px] px-5 pb-28 pt-32 sm:px-8 sm:pb-32 sm:pt-36 lg:px-12 lg:pb-40 lg:pt-44">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
        {/* Text – bewusst schmal und links */}
        <div className="lg:col-span-5 lg:pt-6">
          <Badge className="rounded-none border border-light-border bg-[hsl(0_0%_100%/0.7)] px-4 py-1.5 font-display text-[10px] font-light uppercase tracking-[0.3em] text-light-muted shadow-none hover:bg-[hsl(0_0%_100%/0.7)]">
            Über uns
          </Badge>

          <h2 className="mt-7 font-display text-3xl font-light leading-tight tracking-tight text-light-foreground sm:text-4xl lg:text-5xl">
            Willkommen bei
            <br />
            Vision&nbsp;One.
          </h2>

          <div
            aria-hidden
            className="mt-7 h-px w-24 bg-[linear-gradient(90deg,hsl(222_90%_45%),hsl(265_70%_58%/0.15))]"
          />

          <p className="mt-7 max-w-md text-sm font-light leading-relaxed text-light-muted sm:text-base">
            Platzhaltertext: Hier erzählen wir kurz, wer hinter Vision One
            steckt – ein kleines Team aus Designerinnen, Entwicklern und
            Marketern, das Websites nicht als Projekt, sondern als Partnerschaft
            versteht.
          </p>

          <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-light-muted sm:text-base">
            Platzhaltertext: Zwei bis drei Sätze über Haltung, Arbeitsweise und
            warum Kundinnen und Kunden gerne bleiben. Wird später durch den
            finalen Text ersetzt.
          </p>

          <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-light-border pt-8">
            {FACTS.map((fact) => (
              <div key={fact.label} className="flex flex-col">
                <dt className="font-display text-2xl font-light tracking-tight text-light-foreground sm:text-3xl">
                  {fact.value}
                </dt>
                <dd className="mt-1.5 text-[11px] font-light uppercase tracking-[0.16em] text-light-muted sm:text-xs">
                  {fact.label}
                </dd>
              </div>
            ))}
          </dl>

          <a
            href="#leistungen"
            className="group mt-10 inline-flex items-center gap-2 font-display text-sm font-light tracking-wide text-[hsl(222_90%_42%)] transition-colors duration-300 hover:text-[hsl(222_90%_32%)]"
          >
            Mehr über unsere Arbeitsweise
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

        {/* Bildfeld – versetzt, ragt aus dem Raster */}
        <div className="lg:col-span-7">
          <div className="relative mb-14 sm:mb-16 lg:-mt-16 lg:mb-0 lg:translate-x-6">
            {/* Platzhalter Teamfoto (Stockfoto bis echtes Teamfoto vorliegt) */}
            <div className="relative overflow-hidden border border-light-border bg-[hsl(0_0%_100%/0.55)] shadow-[0_30px_70px_-40px_hsl(225_40%_25%/0.55)]">
              <div className="relative aspect-[4/3] max-h-[440px] w-full">
                <img
                  src="https://images.unsplash.com/photo-1631623843714-3b91f30ec1dc?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Hände tippen auf einer Laptop-Tastatur – Vision One Team bei der Arbeit"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Parallelogramm-Akzent (Markengeometrie) */}
            <div
              aria-hidden
              className="absolute -right-3 -top-6 hidden h-16 w-40 bg-gradient-cta opacity-90 lg:block"
              style={{ transform: "skewX(-14deg)" }}
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);
